import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { locationsAPI, organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, ArrowLeft, Loader2, Save, Truck, Zap, Wifi, Cable, Package } from 'lucide-react';

const LOAD_IN_TYPES = [
  { id: 'Loading Dock', label: 'Loading Dock' },
  { id: 'Street', label: 'Street' },
];

const LOCATION_TYPES = [
  { id: 'venue', label: 'Venue' },
  { id: 'warehouse', label: 'AVF Warehouse' },
  { id: 'vendor', label: 'Vendor' },
  { id: 'other', label: 'Other' },
];

const VenueForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isViewer) { navigate('/locations'); return; }
  }, [isViewer, navigate]);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [form, setForm] = useState({
    location_name: '',
    location_type: 'venue',
    organization_id: searchParams.get('organization_id') || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    load_in_type: [],
    load_in_address: '',
    dock_notes: '',
    dock_measurements: '',
    elevator_sizes: '',
    push_notes: '',
    av_vendor: '',
    rigging_vendor: '',
    power_vendor: '',
    internet_vendor: '',
    rigging_notes: '',
    power_notes: '',
    internet_notes: '',
    available_equipment: '',
    union_requirements: '',
    other_notes: '',
    lessons_learned: '',
    // Call Sheet fields
    parking_options: '',
    nearest_er_name: '',
    nearest_er_address: '',
    nearest_er_phone: '',
  });

  useEffect(() => {
    loadOrganizations();
    if (isEditing) {
      loadLocation();
    }
  }, [id]);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.list({ limit: 1000 });
      // Handle paginated response format
      setOrganizations(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to load organizations');
    }
  };

  const loadLocation = async () => {
    try {
      const response = await locationsAPI.get(id);
      const data = response.data;
      setForm({
        location_name: data.location_name || '',
        location_type: data.location_type || 'venue',
        organization_id: data.organization_id || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code || '',
        load_in_type: data.load_in_type || [],
        load_in_address: data.load_in_address || '',
        dock_notes: data.dock_notes || '',
        dock_measurements: data.dock_measurements || '',
        elevator_sizes: data.elevator_sizes || '',
        push_notes: data.push_notes || '',
        av_vendor: data.av_vendor || '',
        rigging_vendor: data.rigging_vendor || '',
        power_vendor: data.power_vendor || '',
        internet_vendor: data.internet_vendor || '',
        rigging_notes: data.rigging_notes || '',
        power_notes: data.power_notes || '',
        internet_notes: data.internet_notes || '',
        available_equipment: data.available_equipment || '',
        union_requirements: data.union_requirements || '',
        other_notes: data.other_notes || '',
        lessons_learned: data.lessons_learned || '',
        // Call Sheet fields
        parking_options: data.parking_options || '',
        nearest_er_name: data.nearest_er_name || '',
        nearest_er_address: data.nearest_er_address || '',
        nearest_er_phone: data.nearest_er_phone || '',
      });
    } catch (error) {
      toast.error('Failed to load location');
      navigate('/locations');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadInToggle = (typeId) => {
    setForm((prev) => ({
      ...prev,
      load_in_type: prev.load_in_type.includes(typeId)
        ? prev.load_in_type.filter((t) => t !== typeId)
        : [...prev.load_in_type, typeId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.location_name.trim()) {
      toast.error('Location name is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        organization_id: form.organization_id === "none" || !form.organization_id ? null : form.organization_id,
      };

      if (isEditing) {
        await locationsAPI.update(id, data);
        toast.success('Location updated');
      } else {
        const response = await locationsAPI.create(data);
        toast.success('Location created');
        navigate(`/locations/${response.data.id}`);
        return;
      }
      navigate(`/locations/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="location-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={isEditing ? `/locations/${id}` : '/locations'}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <MapPin className="h-8 w-8 text-amber-400" />
            {isEditing ? 'Edit Location' : 'New Location'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location_name">Location Name *</Label>
                <Input
                  id="location_name"
                  value={form.location_name}
                  onChange={(e) => setForm({ ...form, location_name: e.target.value })}
                  placeholder="Enter location name"
                  className="bg-background/50"
                  data-testid="location-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location_type">Location Type</Label>
                <Select
                  value={form.location_type}
                  onValueChange={(value) => setForm({ ...form, location_type: value })}
                >
                  <SelectTrigger className="bg-background/50" data-testid="location-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization_id">Organization</Label>
                <Select
                  value={form.organization_id || "none"}
                  onValueChange={(value) => setForm({ ...form, organization_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-background/50" data-testid="organization-select">
                    <SelectValue placeholder="Select organization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No organization</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.organization_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-background/50"
                data-testid="venue-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 Main St"
                className="bg-background/50"
                data-testid="venue-address-input"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2 col-span-2 md:col-span-1">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Seattle"
                  className="bg-background/50"
                  data-testid="venue-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  placeholder="WA"
                  className="bg-background/50"
                  data-testid="venue-state-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  value={form.zip_code}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                  placeholder="98101"
                  className="bg-background/50"
                  data-testid="venue-zip-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Load-In / Logistics */}
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-cyan-400" />
              Load-In / Logistics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Load-In Type</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                {LOAD_IN_TYPES.map((type) => (
                  <div key={type.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={type.id}
                      checked={form.load_in_type.includes(type.id)}
                      onCheckedChange={() => handleLoadInToggle(type.id)}
                      data-testid={`load-in-${type.id.toLowerCase().replace(' ', '-')}`}
                    />
                    <Label htmlFor={type.id} className="cursor-pointer">
                      {type.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="load_in_address">Load-In Address (if different)</Label>
              <Input
                id="load_in_address"
                value={form.load_in_address}
                onChange={(e) => setForm({ ...form, load_in_address: e.target.value })}
                placeholder="Back entrance, Loading Bay 3..."
                className="bg-background/50"
                data-testid="load-in-address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dock_measurements">Dock Measurements</Label>
                <Textarea
                  id="dock_measurements"
                  value={form.dock_measurements}
                  onChange={(e) => setForm({ ...form, dock_measurements: e.target.value })}
                  placeholder="Height: 14ft, Width: 12ft..."
                  className="bg-background/50 min-h-[80px] font-mono text-sm"
                  data-testid="dock-measurements-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="elevator_sizes">Elevator Sizes</Label>
                <Textarea
                  id="elevator_sizes"
                  value={form.elevator_sizes}
                  onChange={(e) => setForm({ ...form, elevator_sizes: e.target.value })}
                  placeholder="Freight: 8x10x12, Passenger: 6x8..."
                  className="bg-background/50 min-h-[80px] font-mono text-sm"
                  data-testid="elevator-sizes-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dock_notes">Dock Notes</Label>
              <Textarea
                id="dock_notes"
                value={form.dock_notes}
                onChange={(e) => setForm({ ...form, dock_notes: e.target.value })}
                placeholder="Scheduling requirements, restrictions..."
                className="bg-background/50 min-h-[80px]"
                data-testid="dock-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="push_notes">Push Route Notes</Label>
              <Textarea
                id="push_notes"
                value={form.push_notes}
                onChange={(e) => setForm({ ...form, push_notes: e.target.value })}
                placeholder="Path from dock to event space..."
                className="bg-background/50 min-h-[80px]"
                data-testid="push-notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* House Vendors */}
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>House Vendors</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="av_vendor">AV Vendor</Label>
                <Input
                  id="av_vendor"
                  value={form.av_vendor}
                  onChange={(e) => setForm({ ...form, av_vendor: e.target.value })}
                  placeholder="In-house AV company"
                  className="bg-background/50"
                  data-testid="av-vendor-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rigging_vendor">Rigging Vendor</Label>
                <Input
                  id="rigging_vendor"
                  value={form.rigging_vendor}
                  onChange={(e) => setForm({ ...form, rigging_vendor: e.target.value })}
                  placeholder="Approved rigging company"
                  className="bg-background/50"
                  data-testid="rigging-vendor-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="power_vendor">Power Vendor</Label>
                <Input
                  id="power_vendor"
                  value={form.power_vendor}
                  onChange={(e) => setForm({ ...form, power_vendor: e.target.value })}
                  placeholder="Electrical services provider"
                  className="bg-background/50"
                  data-testid="power-vendor-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="internet_vendor">Internet Vendor</Label>
                <Input
                  id="internet_vendor"
                  value={form.internet_vendor}
                  onChange={(e) => setForm({ ...form, internet_vendor: e.target.value })}
                  placeholder="Internet/network provider"
                  className="bg-background/50"
                  data-testid="internet-vendor-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Notes */}
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Technical Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rigging_notes" className="flex items-center gap-2">
                <Cable className="h-4 w-4 text-cyan-400" />
                Rigging Notes
              </Label>
              <Textarea
                id="rigging_notes"
                value={form.rigging_notes}
                onChange={(e) => setForm({ ...form, rigging_notes: e.target.value })}
                placeholder="Rigging points, weight limits, requirements..."
                className="bg-background/50 min-h-[100px]"
                data-testid="rigging-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="power_notes" className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-400" />
                Power Notes
              </Label>
              <Textarea
                id="power_notes"
                value={form.power_notes}
                onChange={(e) => setForm({ ...form, power_notes: e.target.value })}
                placeholder="Available power, outlets, distribution..."
                className="bg-background/50 min-h-[100px]"
                data-testid="power-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="internet_notes" className="flex items-center gap-2">
                <Wifi className="h-4 w-4 text-cyan-400" />
                Internet / Streaming Notes
              </Label>
              <Textarea
                id="internet_notes"
                value={form.internet_notes}
                onChange={(e) => setForm({ ...form, internet_notes: e.target.value })}
                placeholder="Bandwidth, WiFi, hardline options..."
                className="bg-background/50 min-h-[100px]"
                data-testid="internet-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="available_equipment" className="flex items-center gap-2">
                <Package className="h-4 w-4 text-emerald-400" />
                Built in / Available Equipment
              </Label>
              <Textarea
                id="available_equipment"
                value={form.available_equipment}
                onChange={(e) => setForm({ ...form, available_equipment: e.target.value })}
                placeholder="Projectors, screens, sound systems, staging, lighting rigs..."
                className="bg-background/50 min-h-[100px]"
                data-testid="available-equipment-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Union & Other Notes */}
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="union_requirements">Union / Labor Requirements</Label>
              <Textarea
                id="union_requirements"
                value={form.union_requirements}
                onChange={(e) => setForm({ ...form, union_requirements: e.target.value })}
                placeholder="Union rules, labor requirements..."
                className="bg-background/50 min-h-[100px]"
                data-testid="union-requirements-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="other_notes">Other Notes</Label>
              <Textarea
                id="other_notes"
                value={form.other_notes}
                onChange={(e) => setForm({ ...form, other_notes: e.target.value })}
                placeholder="Any other important information..."
                className="bg-background/50 min-h-[100px]"
                data-testid="other-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lessons_learned">Lessons Learned</Label>
              <Textarea
                id="lessons_learned"
                value={form.lessons_learned}
                onChange={(e) => setForm({ ...form, lessons_learned: e.target.value })}
                placeholder="What worked, what didn't, tips for future events..."
                className="bg-background/50 min-h-[100px]"
                data-testid="lessons-learned-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Call Sheet Information */}
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Call Sheet Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="parking_options">Parking Options</Label>
              <Textarea
                id="parking_options"
                value={form.parking_options}
                onChange={(e) => setForm({ ...form, parking_options: e.target.value })}
                placeholder="Available parking, rates, locations..."
                className="bg-background/50 min-h-[80px]"
                data-testid="parking-options-input"
              />
            </div>

            <div className="pt-4 border-t border-border/50">
              <p className="text-sm font-medium mb-4">Nearest Emergency Room</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="nearest_er_name">Hospital/ER Name</Label>
                  <Input
                    id="nearest_er_name"
                    value={form.nearest_er_name}
                    onChange={(e) => setForm({ ...form, nearest_er_name: e.target.value })}
                    placeholder="e.g., Overlake Medical Center"
                    className="bg-background/50"
                    data-testid="nearest-er-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nearest_er_address">Address</Label>
                  <Input
                    id="nearest_er_address"
                    value={form.nearest_er_address}
                    onChange={(e) => setForm({ ...form, nearest_er_address: e.target.value })}
                    placeholder="Full address"
                    className="bg-background/50"
                    data-testid="nearest-er-address-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nearest_er_phone">Phone Number</Label>
                  <Input
                    id="nearest_er_phone"
                    value={form.nearest_er_phone}
                    onChange={(e) => setForm({ ...form, nearest_er_phone: e.target.value })}
                    placeholder="(425) 555-1234"
                    className="bg-background/50"
                    data-testid="nearest-er-phone-input"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            asChild
          >
            <Link to={isEditing ? `/locations/${id}` : '/locations'}>
              Cancel
            </Link>
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-venue-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditing ? 'Update' : 'Create'} Venue
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VenueForm;
